/*
 * IANA time zones in the SPA. The server stores current names only (Orbita.SharedKernel.Time.TimeZoneIds), but ICU-based
 * Intl in browsers and Node still reports and lists Kyiv as "Europe/Kiev": every zone the SPA takes from Intl goes
 * through normalizeTimeZone, and every zone it gives to Intl through intlTimeZone.
 */

/** The portal's default time zone (new portals, invited users, the demo company). */
export const DEFAULT_TIME_ZONE = 'Europe/Kyiv';

/** Legacy ids → the current IANA name: tzdata 2022b renamed Kiev, 2022g merged Uzhgorod and Zaporozhye into Kyiv. */
const LEGACY_NAMES: Readonly<Record<string, string>> = {
  'Europe/Kiev': 'Europe/Kyiv',
  'Europe/Uzhgorod': 'Europe/Kyiv',
  'Europe/Zaporozhye': 'Europe/Kyiv',
};

/** The current IANA name of a zone id (trimmed, a legacy name replaced): "Europe/Kiev" → "Europe/Kyiv". */
export function normalizeTimeZone(id: string): string {
  const trimmed = id.trim();
  return LEGACY_NAMES[trimmed] ?? trimmed;
}

function intlKnows(id: string): boolean {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: id }).resolvedOptions().timeZone !== '';
  } catch {
    return false;
  }
}

const intlIds = new Map<string, string>();

/**
 * The id to give Intl for a zone: the zone itself, or — on an engine whose time zone data predates the current name
 * (ICU before 72, e.g. Safari before 16.4) — a legacy name it knows. An id no name of which is known comes back as it is
 * (Intl then throws as it would have).
 */
export function intlTimeZone(id: string): string {
  let known = intlIds.get(id);
  if (known === undefined) {
    known = id;
    if (!intlKnows(id)) {
      const current = normalizeTimeZone(id);
      const alias = [
        current,
        ...Object.keys(LEGACY_NAMES).filter((name) => LEGACY_NAMES[name] === current),
      ].find(intlKnows);
      if (alias) known = alias;
    }
    intlIds.set(id, known);
  }
  return known;
}

/** The browser's own zone under its current name (UTC when the engine reports none). */
export function browserTimeZone(): string {
  return normalizeTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
}

/**
 * Zones offered first in pickers (and the whole list on engines that cannot list theirs): Kyiv and its neighbours,
 * the rest of Europe, the Caucasus and Central Asia, a few world cities, UTC.
 */
export const COMMON_TIME_ZONES: readonly string[] = [
  'Europe/Kyiv',
  'Europe/Warsaw',
  'Europe/Chisinau',
  'Europe/Bucharest',
  'Europe/Vilnius',
  'Europe/Riga',
  'Europe/Tallinn',
  'Europe/Helsinki',
  'Europe/Berlin',
  'Europe/Prague',
  'Europe/Paris',
  'Europe/London',
  'Europe/Istanbul',
  'Europe/Minsk',
  'Europe/Moscow',
  'Asia/Tbilisi',
  'Asia/Yerevan',
  'Asia/Baku',
  'Asia/Almaty',
  'Asia/Tashkent',
  'Asia/Dubai',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'UTC',
];

/** Every zone the engine knows, under current names and without duplicates (the common zones on older engines). */
export function supportedTimeZones(): string[] {
  let zones: readonly string[];
  try {
    zones = Intl.supportedValuesOf('timeZone');
  } catch {
    zones = COMMON_TIME_ZONES;
  }
  return [...new Set(zones.map(normalizeTimeZone))];
}
